import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Shield, Users, Calendar, Building, Briefcase, Heart, ShoppingBag, Ticket, BarChart,
  ArrowRight, Globe, Check
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Shield, Users, Calendar, Building, Briefcase, Heart, ShoppingBag, Ticket, BarChart
};

const PlatformPage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [platformRes, settingsRes] = await Promise.all([
          axios.get(`${API}/cms/platform`),
          axios.get(`${API}/cms/settings`)
        ]);
        setContent(platformRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Failed to load platform content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const { modules } = content || {};

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-medium mb-6">
              Technology Platform
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              The Complete Sports<br />Management Platform
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              A comprehensive digital ecosystem built to transform how sports organizations operate, 
              engage fans, and drive growth across Africa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {modules?.map((module, idx) => {
              const IconComponent = iconMap[module.icon] || Globe;
              const isEven = idx % 2 === 0;
              
              return (
                <motion.div
                  key={module.id}
                  id={module.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}
                >
                  <div className={isEven ? 'order-1' : 'order-1 lg:order-2'}>
                    <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-3xl font-heading font-bold text-white mb-4">
                      {module.name}
                    </h2>
                    <p className="text-white/70 text-lg leading-relaxed mb-6">
                      {module.description}
                    </p>
                    
                    {module.features && (
                      <ul className="space-y-3 mb-8">
                        {JSON.parse(module.features || '[]').map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-white/80">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-primary hover:underline"
                    >
                      Learn more about {module.name} <ArrowRight size={18} />
                    </Link>
                  </div>
                  
                  <div className={isEven ? 'order-2' : 'order-2 lg:order-1'}>
                    <div className="relative">
                      <div className="aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center">
                        <IconComponent className="w-24 h-24 text-white/10" />
                      </div>
                      <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-primary/20 rounded-2xl" />
                      <div className="absolute -top-4 -left-4 w-16 h-16 bg-secondary/20 rounded-xl" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-background to-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready to Transform Your Organization?
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Get started with Michezonet and join the future of African sports.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              Request Demo <ArrowRight size={18} />
            </Link>
            <Link
              to="/dashboard"
              className="px-8 py-4 border border-white/20 text-white rounded-xl hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
            >
              Access Dashboard
            </Link>
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default PlatformPage;
